import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  FileText, Plus, Edit3, Trash2, Copy, Loader2, Pill, Heart, Phone, ChevronDown, ChevronUp, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  name: '', description: '',
  care_needs: [''],
  medications: [{ name: '', dosage: '', frequency: '', time: '' }],
  emergency_contacts: [{ name: '', phone: '', relationship: '' }],
  preferences: { dietary: '', mobility: '', communication: '' },
};

export default function CarePlanTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [applyOpen, setApplyOpen] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [applying, setApplying] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { fetchTemplates(); fetchClients(); }, []);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/care-plan-templates`, { withCredentials: true });
      setTemplates(data);
    } catch { toast.error('Failed to load templates'); }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/clinical/my-clients`, { withCredentials: true });
      setClients(Array.isArray(data) ? data : []);
    } catch { setClients([]); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, care_needs: [''], medications: [{ name: '', dosage: '', frequency: '', time: '' }], emergency_contacts: [{ name: '', phone: '', relationship: '' }], preferences: { dietary: '', mobility: '', communication: '' } });
    setDialogOpen(true);
  };

  const openEdit = (tmpl) => {
    setEditing(tmpl.id);
    setForm({
      name: tmpl.name || '',
      description: tmpl.description || '',
      care_needs: tmpl.care_needs?.length ? tmpl.care_needs : [''],
      medications: tmpl.medications?.length ? tmpl.medications : [{ name: '', dosage: '', frequency: '', time: '' }],
      emergency_contacts: tmpl.emergency_contacts?.length ? tmpl.emergency_contacts : [{ name: '', phone: '', relationship: '' }],
      preferences: tmpl.preferences || { dietary: '', mobility: '', communication: '' },
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Template name is required');
    setSaving(true);
    const payload = {
      ...form,
      care_needs: form.care_needs.filter(n => n.trim()),
      medications: form.medications.filter(m => m.name?.trim()),
      emergency_contacts: form.emergency_contacts.filter(c => c.name?.trim()),
    };
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/care-plan-templates/${editing}`, payload, { withCredentials: true });
        toast.success('Template updated');
      } else {
        await axios.post(`${API_URL}/api/care-plan-templates`, payload, { withCredentials: true });
        toast.success('Template created');
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/care-plan-templates/${id}`, { withCredentials: true });
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  const handleApply = async (templateId) => {
    if (!selectedClient) return toast.error('Select a client');
    setApplying(true);
    try {
      await axios.post(`${API_URL}/api/care-plan-templates/${templateId}/apply`, { client_id: selectedClient }, { withCredentials: true });
      toast.success('Care plan created from template');
      setApplyOpen(null);
      setSelectedClient('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to apply template'); }
    setApplying(false);
  };

  const addCareNeed = () => setForm(f => ({ ...f, care_needs: [...f.care_needs, ''] }));
  const addMed = () => setForm(f => ({ ...f, medications: [...f.medications, { name: '', dosage: '', frequency: '', time: '' }] }));
  const addContact = () => setForm(f => ({ ...f, emergency_contacts: [...f.emergency_contacts, { name: '', phone: '', relationship: '' }] }));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="care-plan-templates-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Care Plan Templates</h1>
            <p className="text-sm text-muted-foreground mt-1">Save and reuse common care configurations</p>
          </div>
          <Button className="btn-primary" onClick={openCreate} data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-2" />New Template
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
        ) : templates.length === 0 ? (
          <Card className="glass-card border-border">
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <p className="text-foreground font-medium">No templates yet</p>
              <p className="text-sm text-muted-foreground mt-1 mb-4">Create a template to speed up care plan creation for new clients</p>
              <Button className="btn-primary" onClick={openCreate}>Create First Template</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {templates.map(tmpl => (
              <Card key={tmpl.id} className="glass-card border-border" data-testid={`template-${tmpl.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(expanded === tmpl.id ? null : tmpl.id)}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#00A6A0] flex-shrink-0" />
                        <h3 className="text-foreground font-medium truncate">{tmpl.name}</h3>
                        {expanded === tmpl.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      {tmpl.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{tmpl.description}</p>}
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {tmpl.care_needs?.length > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0]">{tmpl.care_needs.length} care needs</span>}
                        {tmpl.medications?.length > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF6F61]/10 text-[#FF6F61]">{tmpl.medications.length} medications</span>}
                        {tmpl.emergency_contacts?.length > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">{tmpl.emergency_contacts.length} contacts</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button size="sm" variant="outline" className="h-8 text-xs border-[#00A6A0]/30 text-[#00A6A0]"
                        onClick={() => { setApplyOpen(tmpl.id); setSelectedClient(''); }}
                        data-testid={`apply-template-${tmpl.id}`}>
                        <Copy className="w-3 h-3 mr-1" />Use
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-muted-foreground" onClick={() => openEdit(tmpl)} data-testid={`edit-template-${tmpl.id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:text-red-300" onClick={() => handleDelete(tmpl.id)} data-testid={`delete-template-${tmpl.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {expanded === tmpl.id && (
                    <div className="mt-4 pt-4 border-t border-border/40 space-y-3">
                      {tmpl.care_needs?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1"><Heart className="w-3 h-3" />Care Needs</p>
                          <div className="flex flex-wrap gap-1.5">
                            {tmpl.care_needs.map((n, i) => <span key={i} className="text-xs px-2 py-1 rounded-md bg-muted border border-border text-foreground">{n}</span>)}
                          </div>
                        </div>
                      )}
                      {tmpl.medications?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1"><Pill className="w-3 h-3" />Medications</p>
                          <div className="space-y-1">
                            {tmpl.medications.map((m, i) => (
                              <div key={i} className="text-xs text-foreground p-2 rounded-md bg-muted border border-border">
                                <span className="font-medium">{m.name}</span>
                                {m.dosage && <span className="text-muted-foreground ml-2">{m.dosage}</span>}
                                {m.frequency && <span className="text-muted-foreground ml-2">({m.frequency})</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {tmpl.emergency_contacts?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1"><Phone className="w-3 h-3" />Emergency Contacts</p>
                          {tmpl.emergency_contacts.map((c, i) => (
                            <p key={i} className="text-xs text-foreground">{c.name} — {c.phone} {c.relationship && `(${c.relationship})`}</p>
                          ))}
                        </div>
                      )}
                      <p className="text-[10px] text-muted-foreground">Created by {tmpl.created_by_name || 'Admin'} on {new Date(tmpl.created_at).toLocaleDateString()}</p>
                    </div>
                  )}

                  {/* Apply to client dialog inline */}
                  {applyOpen === tmpl.id && (
                    <div className="mt-3 pt-3 border-t border-border/40 flex items-end gap-2 flex-wrap" data-testid={`apply-panel-${tmpl.id}`}>
                      <div className="flex-1 min-w-[180px]">
                        <label className="text-[10px] text-muted-foreground block mb-1">Select Client</label>
                        <Select value={selectedClient} onValueChange={setSelectedClient}>
                          <SelectTrigger className="bg-muted border-border text-foreground h-9 text-xs" data-testid="apply-client-select">
                            <SelectValue placeholder="Choose a client..." />
                          </SelectTrigger>
                          <SelectContent className="bg-background border-border">
                            {clients.map(c => (
                              <SelectItem key={c.id || c.usic} value={c.id}>{c.name} ({c.usic})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button size="sm" className="btn-primary h-9 text-xs" onClick={() => handleApply(tmpl.id)} disabled={applying} data-testid="confirm-apply-btn">
                        {applying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                        Create Plan
                      </Button>
                      <Button size="sm" variant="ghost" className="h-9 text-xs text-muted-foreground" onClick={() => setApplyOpen(null)}>Cancel</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Template Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">{editing ? 'Edit Template' : 'New Care Plan Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Template Name *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Elderly Home Care, Post-Surgery Recovery"
                className="bg-muted border-border text-foreground" data-testid="template-name-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of this care plan type..."
                className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] resize-none"
                data-testid="template-desc-input" />
            </div>

            {/* Care Needs */}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Care Needs</label>
              {form.care_needs.map((need, i) => (
                <div key={i} className="flex gap-2 mb-1.5">
                  <Input value={need}
                    onChange={e => { const arr = [...form.care_needs]; arr[i] = e.target.value; setForm(f => ({ ...f, care_needs: arr })); }}
                    placeholder="e.g. Personal hygiene assistance"
                    className="bg-muted border-border text-foreground text-sm" />
                  {form.care_needs.length > 1 && (
                    <Button size="sm" variant="ghost" className="text-red-400 h-9 px-2"
                      onClick={() => setForm(f => ({ ...f, care_needs: f.care_needs.filter((_, j) => j !== i) }))}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))}
              <Button size="sm" variant="ghost" className="text-[#00A6A0] text-xs" onClick={addCareNeed}>
                <Plus className="w-3 h-3 mr-1" />Add Care Need
              </Button>
            </div>

            {/* Medications */}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Medications</label>
              {form.medications.map((med, i) => (
                <div key={i} className="grid grid-cols-4 gap-1.5 mb-1.5">
                  <Input value={med.name} placeholder="Name"
                    onChange={e => { const arr = [...form.medications]; arr[i] = { ...arr[i], name: e.target.value }; setForm(f => ({ ...f, medications: arr })); }}
                    className="bg-muted border-border text-foreground text-xs" />
                  <Input value={med.dosage} placeholder="Dosage"
                    onChange={e => { const arr = [...form.medications]; arr[i] = { ...arr[i], dosage: e.target.value }; setForm(f => ({ ...f, medications: arr })); }}
                    className="bg-muted border-border text-foreground text-xs" />
                  <Input value={med.frequency} placeholder="Frequency"
                    onChange={e => { const arr = [...form.medications]; arr[i] = { ...arr[i], frequency: e.target.value }; setForm(f => ({ ...f, medications: arr })); }}
                    className="bg-muted border-border text-foreground text-xs" />
                  <div className="flex gap-1">
                    <Input value={med.time} placeholder="Time"
                      onChange={e => { const arr = [...form.medications]; arr[i] = { ...arr[i], time: e.target.value }; setForm(f => ({ ...f, medications: arr })); }}
                      className="bg-muted border-border text-foreground text-xs flex-1" />
                    {form.medications.length > 1 && (
                      <Button size="sm" variant="ghost" className="text-red-400 h-9 px-1"
                        onClick={() => setForm(f => ({ ...f, medications: f.medications.filter((_, j) => j !== i) }))}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <Button size="sm" variant="ghost" className="text-[#00A6A0] text-xs" onClick={addMed}>
                <Plus className="w-3 h-3 mr-1" />Add Medication
              </Button>
            </div>

            {/* Emergency Contacts */}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Emergency Contacts</label>
              {form.emergency_contacts.map((c, i) => (
                <div key={i} className="grid grid-cols-3 gap-1.5 mb-1.5">
                  <Input value={c.name} placeholder="Name"
                    onChange={e => { const arr = [...form.emergency_contacts]; arr[i] = { ...arr[i], name: e.target.value }; setForm(f => ({ ...f, emergency_contacts: arr })); }}
                    className="bg-muted border-border text-foreground text-xs" />
                  <Input value={c.phone} placeholder="Phone"
                    onChange={e => { const arr = [...form.emergency_contacts]; arr[i] = { ...arr[i], phone: e.target.value }; setForm(f => ({ ...f, emergency_contacts: arr })); }}
                    className="bg-muted border-border text-foreground text-xs" />
                  <div className="flex gap-1">
                    <Input value={c.relationship} placeholder="Relation"
                      onChange={e => { const arr = [...form.emergency_contacts]; arr[i] = { ...arr[i], relationship: e.target.value }; setForm(f => ({ ...f, emergency_contacts: arr })); }}
                      className="bg-muted border-border text-foreground text-xs flex-1" />
                    {form.emergency_contacts.length > 1 && (
                      <Button size="sm" variant="ghost" className="text-red-400 h-9 px-1"
                        onClick={() => setForm(f => ({ ...f, emergency_contacts: f.emergency_contacts.filter((_, j) => j !== i) }))}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <Button size="sm" variant="ghost" className="text-[#00A6A0] text-xs" onClick={addContact}>
                <Plus className="w-3 h-3 mr-1" />Add Contact
              </Button>
            </div>

            {/* Preferences */}
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Preferences</label>
              <div className="grid grid-cols-3 gap-2">
                <Input value={form.preferences.dietary || ''} placeholder="Dietary"
                  onChange={e => setForm(f => ({ ...f, preferences: { ...f.preferences, dietary: e.target.value } }))}
                  className="bg-muted border-border text-foreground text-xs" />
                <Input value={form.preferences.mobility || ''} placeholder="Mobility"
                  onChange={e => setForm(f => ({ ...f, preferences: { ...f.preferences, mobility: e.target.value } }))}
                  className="bg-muted border-border text-foreground text-xs" />
                <Input value={form.preferences.communication || ''} placeholder="Communication"
                  onChange={e => setForm(f => ({ ...f, preferences: { ...f.preferences, communication: e.target.value } }))}
                  className="bg-muted border-border text-foreground text-xs" />
              </div>
            </div>

            <Button className="w-full btn-primary" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
